import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import ListingImageSlideshow from "../components/ListingImageSlideshow";
import DiscountPriceBreakdown from "../components/DiscountPriceBreakdown";
import OfferPaymentBadges from "../components/OfferPaymentBadges";
import FollowItemButton, { FollowItemCountdown } from "../components/FollowItemButton";
import CopyToClipboardComponent from "./CopyToClipboardButton";
import OfferBuyButton from "../components/OfferBuyButton";
import { getDomainFromUrl } from "./homeUtils";
import { toJsDate } from "./eventsUtils";
import { brandColors } from "../theme";

// Ficha pública de una oferta (/offers/:offerId): se ve sin iniciar sesión
// y figura en el sitemap. El cupón suma usos en Firestore, que requiere
// sesión, así que a los visitantes se les pide iniciar sesión para verlo.
const OfferDetail = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checked, user } = useAuthUser();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, "offers", offerId))
      .then((snap) => active && setOffer(snap.exists() ? { id: snap.id, ...snap.data() } : null))
      .catch((e) => console.error("Error al obtener la oferta:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [offerId]);

  const title = offer?.product || offer?.title;
  const discount = Number(offer?.offer) || 0;
  const createdAt = toJsDate(offer?.createdAt);

  return (
    <PublicPageLayout
      title={title || <T>Oferta</T>}
      loading={loading || !checked}
      notFound={!offer && <T>No se encontró la oferta.</T>}
      isGuest={!user}
      showAd
    >
      <Stack spacing={2.5}>
        <ListingImageSlideshow
          images={offer?.images}
          alt={title}
          height={{ xs: 240, sm: 360 }}
          autoPlayMs={5000}
          PlaceholderIcon={LocalOfferIcon}
          placeholderText={<T>No hay imágenes disponibles</T>}
        />

        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {title}
          </Typography>
          {offer?.name && (
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
              <T>Publicado por</T> {offer.name}
              {createdAt && ` · ${createdAt.toLocaleDateString()}`}
            </Typography>
          )}
        </Box>

        {offer?.price && (
          <DiscountPriceBreakdown
            originalPrice={Number(offer.price)}
            discountPercent={discount}
            finalPrice={Number(offer.price) * (1 - discount / 100)}
          />
        )}

        <OfferPaymentBadges offer={offer} />

        {offer && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FollowItemButton type="offer" item={offer} variant="button" />
            <FollowItemCountdown type="offer" item={offer} sx={{ alignSelf: "center" }} />
          </Stack>
        )}

        {offer?.description && (
          <Typography sx={{ whiteSpace: "pre-line", color: "text.primary" }}>{offer.description}</Typography>
        )}

        {offer?.cupon &&
          (user ? (
            <CopyToClipboardComponent textToCopy={offer.cupon} offer={offer} />
          ) : (
            <Button variant="outlined" onClick={() => navigate("/login", { state: { from: location } })}>
              <T>Iniciá sesión para ver el cupón</T>
            </Button>
          ))}

        {offer && <OfferBuyButton offer={offer} sx={{ py: 1.25 }} />}

        {offer?.url && (
          <Button
            variant="contained"
            size="large"
            href={offer.url.startsWith("http") ? offer.url : `http://${offer.url}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: brandColors.action, "&:hover": { bgcolor: brandColors.actionHover } }}
          >
            <T>Comprar en</T> {getDomainFromUrl(offer.url)}
          </Button>
        )}
      </Stack>
    </PublicPageLayout>
  );
};

export default OfferDetail;
